import { baseApi } from "@/services/api";
import { store } from "@/app/store";
import {
  applyLifecycleCountDelta,
  isStaleCountVersusExpected,
  lifecycleCountDelta,
  TRACKS_RECONCILE_DELAY_MS,
  TRACKS_RECONCILE_MAX_ATTEMPTS,
  TRACKS_RECONCILE_RETRY_MS,
  type LifecycleCountOp,
  type LifecycleCountSnapshot,
} from "@/features/tracks/lifecycleCountPatch";
import {
  scheduleProcessingJobsInvalidateAfterSongLifecycleMirror,
  SONG_LIFECYCLE_MIRROR_PROCESSING_INVALIDATE_DELAYS_MS,
} from "@/features/processing/processingApi";

// Canonical Tracks API client (SL1 + MDL Song Library lifecycle views).
//
// Backed by kk-admin-sa-music-catalog through `/tracks`. Reads hit
// kk-track-catalog-projection-${stage}. Tracks are created ONLY by the
// processing pipeline — this client is read + lifecycle only.
//
// Lifecycle mutations patch list + count caches optimistically and do NOT
// immediately invalidate LIST (projection race / stale count overwrite).
// Song Library schedules a delayed LIST reconcile after success.
//
// Song History reads a separate summary projection (shSummaryMat). Archive /
// Restore / Permanent Delete must invalidate SONG_HISTORY on the same delayed
// ladder as Song Processing so Active/Archived/Deleted tabs + counts refresh
// without a hard reload.

/** Same ladder as Song Processing Lifecycle Mirror / Song PD freshness. */
export const SONG_LIFECYCLE_MIRROR_SONG_HISTORY_INVALIDATE_DELAYS_MS =
  SONG_LIFECYCLE_MIRROR_PROCESSING_INVALIDATE_DELAYS_MS;

let songLifecycleMirrorSongHistoryInvalidateGeneration = 0;

export type SongHistoryLifecycleInvalidateOp =
  | "archive"
  | "restore"
  | "delete";

/**
 * Refresh Song History list + tab counts after Song Archive / Restore /
 * Permanent Delete. RTK invalidateTags only (no polling). Immediate + delayed
 * retries cover summary-projection catch-up after the Track lifecycle write.
 */
export function scheduleSongHistoryInvalidateAfterSongLifecycle(
  dispatch: typeof store.dispatch,
  args: {
    op: SongHistoryLifecycleInvalidateOp;
    trackId?: string;
    trackIds?: string[];
  },
) {
  const ids = Array.from(
    new Set(
      [
        ...(typeof args.trackId === "string" ? [args.trackId] : []),
        ...(Array.isArray(args.trackIds) ? args.trackIds : []),
      ]
        .map((id) => (typeof id === "string" ? id.trim() : ""))
        .filter((id) => id.length > 0),
    ),
  );
  if (!ids.length) return;

  const generation = ++songLifecycleMirrorSongHistoryInvalidateGeneration;
  const tags: Array<{ type: "Tracks"; id: string }> = [
    { type: "Tracks", id: "SONG_HISTORY" },
    ...ids.map((trackId) => ({
      type: "Tracks" as const,
      id: `SONG_HISTORY_${trackId}`,
    })),
  ];
  for (const delayMs of SONG_LIFECYCLE_MIRROR_SONG_HISTORY_INVALIDATE_DELAYS_MS) {
    window.setTimeout(() => {
      if (generation !== songLifecycleMirrorSongHistoryInvalidateGeneration) {
        return;
      }
      dispatch(tracksApi.util.invalidateTags(tags));
    }, delayMs);
  }
}

export type TrackStatus = "active" | "archived";
export type TrackSongType = "kk-original" | "commercial";

export interface Track {
  trackId: string;
  title: string;
  titleLower: string;
  status: TrackStatus;
  playable: boolean;
  songType: TrackSongType | string;
  playbackVersionNumber: number | null;
  playbackArtifactVersionId: string | null;
  approvedObjectKey: string | null;
  checksumApproved?: string | null;
  primaryArtistId: string | null;
  recordLabelId: string | null;
  albumId?: string | null;
  /** Optional Genre taxonomy assignment (Genre catalog v1). */
  genreId?: string | null;
  /** Optional Subgenre (always belongs to genreId). */
  subgenreId?: string | null;
  isrc: string | null;
  /** Operator-declared acquisition Source (iTunes / CD / DMP / SKS-*). */
  mediaSource?: string | null;
  sourceProcessingJobId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  materializedAt?: string;
  /** True when status === archived (explicit operator flag). */
  archived?: boolean;
  /** Soft-archive timestamp (API also aliases deletedAt → archivedAt). */
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: string | null;
  /** Restore stamps — archive history is retained, not erased. */
  restoredAt?: string | null;
  restoredBy?: string | null;
  deletedAt?: string | null;
  deletedBySub?: string | null;
  releaseYear?: number | null;
  createdBySource?: string | null;
}

export interface ListTracksArgs {
  status?: TrackStatus | "all";
  albumId?: string;
  artistId?: string;
  recordLabelId?: string;
  /** Optional Genre filter (Genre catalog v1). */
  genreId?: string;
  /** Optional Subgenre filter — only valid together with genreId. */
  subgenreId?: string;
  trackId?: string;
  limit?: number;
  pageToken?: string;
}

export interface ListTracksResponse {
  tracks: Track[];
  pageToken: string | null;
  limit: number;
  status: string;
  trackId?: string;
}

/** H1/H2 — Song History summary row (one per Track for life until Tombstone Purge). */
export interface TrackSongHistorySummary {
  trackId: string;
  title: string | null;
  artistName: string | null;
  primaryArtistId: string | null;
  albumId: string | null;
  albumTitle: string | null;
  recordLabelId: string | null;
  recordLabelName: string | null;
  songType: string | null;
  originalUploaderSub: string | null;
  originalUploaderEmployeeId: string | null;
  firstUploadedAt: string | null;
  latestActivityAt: string | null;
  latestActivityType: string | null;
  latestActivityLabel: string | null;
  currentOutcome: string | null;
  currentOutcomeLabel: string | null;
  playbackVersionNumber: number | null;
  versionsDisplay: string | null;
  status: string | null;
  playable: boolean;
  songHistoryPath: string;
  permanentlyDeletedAt?: string | null;
  permanentlyDeletedBySub?: string | null;
  permanentDeleteReason?: string | null;
  deletionManifestId?: string | null;
  archivedAt?: string | null;
  restoredAt?: string | null;
}

export interface ListTrackSongHistoryArgs {
  limit?: number;
  pageToken?: string;
  /** active | archived | deleted — required for tabbed list. */
  status?: "active" | "archived" | "deleted";
  trackId?: string;
  qTitle?: string;
  qArtist?: string;
  qUploader?: string;
  firstUploadedFrom?: string;
  firstUploadedTo?: string;
  latestActivityFrom?: string;
  latestActivityTo?: string;
  latestActivityType?: string;
  currentOutcome?: string;
  deletedFrom?: string;
  deletedTo?: string;
  deletedBy?: string;
  qDeletionReason?: string;
  count?: boolean;
}

export interface ListTrackSongHistoryResponse {
  songs: TrackSongHistorySummary[];
  pageToken: string | null;
  limit: number;
}

export interface CountTrackSongHistoryResponse {
  count: number;
  exact: boolean;
}

export interface SongHistoryCountsByStatusResponse {
  active: number;
  archived: number;
  deleted: number;
  exact: boolean;
}

/** Song History Detail aggregate. */
export interface SongHistoryTimelineEvent {
  type: string;
  label: string;
  at: string | null;
  actorSub: string | null;
  actorEmployeeId: string | null;
  reason: string | null;
  jobId: string | null;
  playbackVersionNumber: number | null;
  auditEventId: string | null;
  auditEventType: string | null;
  before: unknown;
  after: unknown;
  source: string | null;
}

export interface SongHistoryAttempt {
  attemptNumber: number;
  jobId: string;
  purpose: string;
  purposeLabel: string;
  outcome: string;
  state: string | null;
  decision: string | null;
  at: string | null;
  jobRecordAvailable?: boolean;
  viewJobPath: string | null;
  jobRecordMessage?: string | null;
}

export interface SongHistoryPlaybackVersion {
  artifactVersionId: string;
  version: number | null;
  versionsDisplay: string | null;
  status: string | null;
  isCurrent: boolean;
  eventLabel: string;
  sourceJobPurpose: string | null;
  sourceProcessingJobId: string | null;
  jobRecordAvailable?: boolean;
  viewJobPath: string | null;
  jobRecordMessage?: string | null;
  createdAt: string | null;
  supersededByArtifactVersionId: string | null;
  approvedObjectKey: string | null;
}

export interface SongHistoryDetailResponse {
  summary: TrackSongHistorySummary & {
    permanentlyDeletedBySub?: string | null;
    permanentDeleteReason?: string | null;
    openSongPath?: string | null;
  };
  timeline: SongHistoryTimelineEvent[];
  attempts: SongHistoryAttempt[];
  playbackHistory: SongHistoryPlaybackVersion[];
  /** Prior / current Channel Programming placements (frozen on Archive / PD). */
  channelMembershipHistory?: ChannelMembershipHistoryEntry[];
  tombstone: {
    permanentlyDeletedAt: string | null;
    permanentlyDeletedBySub: string | null;
    permanentDeleteReason: string | null;
    deletionManifestId: string | null;
  } | null;
  frozenAt?: string | null;
  fromFrozenSnapshot?: boolean;
}

/** Frozen / snapshot Channel Programming placement for Song History Detail. */
export interface ChannelMembershipHistoryEntry {
  channelId?: string | null;
  channelName?: string | null;
  scheduleId?: string | null;
  scheduleSide?: string | null;
  rotationId?: string | null;
  rotationName?: string | null;
  category?: string | null;
  rotationTrackId?: string | null;
  position?: number | null;
  trackTitle?: string | null;
  artistName?: string | null;
  addedAt?: string | null;
  removedAt?: string | null;
  removedBySub?: string | null;
  operation?: string | null;
  cleanupResult?: string | null;
}

export interface TrackChannelImpact {
  trackId: string;
  title?: string | null;
  trackStatus?: string;
  playable?: boolean;
  placementCount: number;
  channelCount: number;
  channels: Array<{
    channelId?: string | null;
    channelName?: string | null;
    placementCount: number;
    scheduleSides?: string[];
    rotations?: Array<{
      rotationId?: string | null;
      rotationName?: string | null;
      category?: string | null;
      scheduleSide?: string | null;
      scheduleId?: string | null;
      rotationTrackId?: string | null;
      position?: number | null;
    }>;
  }>;
  placements?: ChannelMembershipHistoryEntry[];
  continuity?: {
    blocked: boolean;
    blockers: Array<{
      code: string;
      message: string;
      channelId?: string | null;
      channelName?: string | null;
      rotationId?: string | null;
      rotationName?: string | null;
      scheduleSide?: string | null;
    }>;
  };
  blocked: boolean;
  blockers: Array<{
    code: string;
    message: string;
    channelId?: string | null;
    channelName?: string | null;
    rotationId?: string | null;
    rotationName?: string | null;
    scheduleSide?: string | null;
  }>;
  operatorMessage?: string | null;
  checkStatus?: string;
}

export interface CountTracksArgs {
  status?: TrackStatus | "all";
  albumId?: string;
  artistId?: string;
  recordLabelId?: string;
}

export interface CountTracksResponse {
  count: number;
  exact: boolean;
  status: string;
  byStatus?: {
    active: number;
    archived: number;
  };
}

/**
 * Genre catalog v1 — PATCH /tracks/{trackId} taxonomy assignment.
 * Fields are included ONLY when the operator changed them (undefined =
 * not sent); null clears. Server enforces: subgenre must belong to the
 * genre and be active; archived taxonomy cannot be newly assigned;
 * clearing genre clears subgenre.
 */
export interface UpdateTrackGenreArgs {
  trackId: string;
  genreId?: string | null;
  subgenreId?: string | null;
}

export interface ArchiveTrackArgs {
  trackId: string;
}

export interface ArchiveTrackResponse {
  trackId: string;
  status: "archived";
  playable: false;
  deletedAt: string;
  version: number;
  /** Present when DELETE was a no-op because the Track was already archived. */
  alreadyArchived?: boolean;
}

export interface RestoreTrackArgs {
  trackId: string;
}

export interface RestoreTrackResponse {
  trackId: string;
  status: "active";
  playable: boolean;
  restoredAt: string;
  restoredBy?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: string | null;
  version: number;
}

export interface PermanentDeleteStalePointer {
  code: string;
  message: string;
  pointer?: string;
}

export interface PermanentDeletePreflight {
  trackId: string;
  title: string;
  status: string;
  dryRun: true;
  eligible: boolean;
  blockers: Array<{
    code: string;
    message: string;
    count?: number | null;
    channelId?: string | null;
    channelName?: string | null;
    rotationId?: string | null;
    rotationName?: string | null;
    scheduleSide?: string | null;
  }>;
  stalePointers?: PermanentDeleteStalePointer[];
  counts: {
    processingJobs: number;
    approvedArtifactVersions: number;
    playbackObjects: number;
    programmingReferences: number;
    stalePointers?: number;
    channelCount?: number;
  };
  channelImpact?: {
    placementCount: number;
    channelCount: number;
    channels: TrackChannelImpact["channels"];
    operatorMessage?: string | null;
    blocked?: boolean;
    blockers?: Array<{ code: string; message: string }>;
  } | null;
  willRemove: string[];
  willRetain: string[];
  preview?: {
    jobIds: string[];
    artifactVersionIds: string[];
  };
}

export interface PermanentDeleteArgs {
  trackId: string;
  confirmTitle: string;
  confirmTrackId: string;
  reason: string;
}

export interface PermanentDeleteResponse {
  trackId: string;
  purged: boolean;
  status: "deleted";
  deletionManifestId: string;
  permanentlyDeletedAt: string;
  counts?: PermanentDeletePreflight["counts"];
  manifest?: unknown;
  willRetain?: string[];
}

export interface BulkPermanentDeleteArgs {
  ids: string[];
  reason: string;
}

/** Step 7 — Song History Tombstone Purge durable BulkOp. */
export interface SongHistoryTombstonePurgeFilter {
  status?: "deleted";
  qTitle?: string;
  qArtist?: string;
  qUploader?: string;
  firstUploadedFrom?: string;
  firstUploadedTo?: string;
  latestActivityFrom?: string;
  latestActivityTo?: string;
  latestActivityType?: string;
  currentOutcome?: string;
  trackId?: string;
  deletedFrom?: string;
  deletedTo?: string;
  deletedBy?: string;
  qDeletionReason?: string;
}

export interface CreateSongHistoryTombstonePurgeArgs {
  reason: string;
  selectionMode: "ids" | "allMatching";
  ids?: string[];
  excludedIds?: string[];
  filter?: SongHistoryTombstonePurgeFilter;
  idempotencyKey?: string;
}

export interface SongHistoryTombstonePurgeOp {
  bulkOpId: string;
  action?: string;
  status: string;
  selectionMode?: string;
  totalMatched: number;
  counters?: {
    examined?: number;
    succeeded?: number;
    skipped?: number;
    failed?: number;
  };
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  errorMessage?: string | null;
}

export interface BulkPermanentDeleteFailure {
  trackId: string;
  title: string | null;
  ok: false;
  skipped?: boolean;
  message: string;
  retryable: boolean;
  details?: unknown;
}

export interface BulkPermanentDeleteResponse {
  action: string;
  succeeded: number;
  failed: number;
  skipped: number;
  results: Array<{
    trackId: string;
    title?: string | null;
    ok: boolean;
    skipped?: boolean;
    message?: string;
    retryable?: boolean;
    deletionManifestId?: string;
    idempotent?: boolean;
    details?: unknown;
  }>;
}

/** Durable Song Library Track Permanent Delete BulkOp (server-owned). */
export interface CreateTrackPermanentDeleteBulkOpArgs {
  ids: string[];
  reason: string;
  idempotencyKey: string;
  selectionMode?: "ids";
}

export interface TrackPermanentDeleteBulkOpItem {
  trackId: string;
  title?: string | null;
  status: string;
  ok?: boolean;
  skipped?: boolean;
  retryable?: boolean;
  message?: string | null;
  reasonCode?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
}

export interface TrackPermanentDeleteBulkOp {
  bulkOpId: string;
  action?: string;
  status: string;
  selectionMode?: string;
  totalMatched: number;
  counters?: {
    examined?: number;
    succeeded?: number;
    skipped?: number;
    failed?: number;
    retryable?: number;
  };
  results?: TrackPermanentDeleteBulkOpItem[];
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  errorMessage?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

/** Best-effort sourceProcessingJobId from cached Song Library list rows. */
function findCachedTrackSourceJobId(trackId: string): string | null {
  if (!trackId) return null;
  const state = store.getState();
  const cachedArgs = tracksApi.util.selectCachedArgsForQuery(state, "listTracks");
  for (const args of cachedArgs) {
    const entry = tracksApi.endpoints.listTracks.select(args)(state);
    const found = entry.data?.tracks?.find((t) => t.trackId === trackId);
    if (found && typeof found.sourceProcessingJobId === "string") {
      const id = found.sourceProcessingJobId.trim();
      if (id) return id;
    }
  }
  return null;
}

export type SongLibraryListView = "active" | "archived" | "all";

function listTracksViewStatus(
  args: ListTracksArgs | void | null | undefined,
): SongLibraryListView {
  const status = args && args.status;
  if (status === "archived" || status === "all") return status;
  return "active";
}

function findCachedTrackRow(trackId: string): Track | null {
  if (!trackId) return null;
  const state = store.getState();
  const cachedArgs = tracksApi.util.selectCachedArgsForQuery(state, "listTracks");
  for (const args of cachedArgs) {
    const entry = tracksApi.endpoints.listTracks.select(args)(state);
    const found = entry.data?.tracks?.find((t) => t.trackId === trackId);
    if (found) return { ...found };
  }
  return null;
}

/**
 * Pure list transform for Song Library lifecycle cache patches.
 * Restoring/archiving one Song must remove ONLY that Song from the source
 * tab — sibling rows on the same cached page must be preserved.
 */
export function applyLifecycleMembershipToTracks(
  tracks: readonly Track[],
  view: SongLibraryListView,
  op: "archive" | "restore" | "delete",
  trackId: string,
  row: Track | null,
): Track[] {
  const id = typeof trackId === "string" ? trackId.trim() : "";
  if (!id) return tracks.slice();
  const without = tracks.filter((t) => t.trackId !== id);

  if (op === "delete") return without;

  if (op === "restore") {
    // Source tab: drop only the restored Song. Never rewrite the page.
    if (view === "archived") return without;
    if (!row) return without;
    const upsert: Track = {
      ...row,
      trackId: id,
      status: "active",
      archived: false,
    };
    const idx = without.findIndex((t) => t.trackId === id);
    if (idx >= 0) {
      const next = without.slice();
      next[idx] = { ...without[idx], ...upsert };
      return next;
    }
    return [upsert, ...without];
  }

  // archive
  if (view === "active") return without;
  if (!row) return without;
  const upsert: Track = {
    ...row,
    trackId: id,
    status: "archived",
    archived: true,
    playable: false,
  };
  const idx = without.findIndex((t) => t.trackId === id);
  if (idx >= 0) {
    const next = without.slice();
    next[idx] = { ...without[idx], ...upsert };
    return next;
  }
  return [upsert, ...without];
}

/**
 * Enforce exclusive Active ↔ Archived membership across every cached
 * listTracks page. A Song must never appear in both lifecycle tabs.
 *
 * Never seeds a cold destination page with a single-row stub — that replaced
 * full Active/Archived pages and could leave sibling Songs missing until
 * refresh. Destination tabs that are cold refetch on next visit instead.
 */
export function applyTrackLifecycleListMembership(
  dispatch: typeof store.dispatch,
  args: {
    trackId: string;
    op: "archive" | "restore" | "delete";
    row?: Track | null;
  },
) {
  const trackId = typeof args.trackId === "string" ? args.trackId.trim() : "";
  if (!trackId) return;
  const op = args.op;
  const row = args.row ? { ...args.row, trackId } : findCachedTrackRow(trackId);

  // Snapshot args before writes — mutating the cache mid-loop is unsafe.
  const cachedArgs = [
    ...tracksApi.util.selectCachedArgsForQuery(store.getState(), "listTracks"),
  ];
  for (const listArgs of cachedArgs) {
    const entry = tracksApi.endpoints.listTracks.select(listArgs)(
      store.getState(),
    );
    const current = entry.data;
    if (!current || !Array.isArray(current.tracks)) continue;
    const view = listTracksViewStatus(listArgs);
    const nextTracks = applyLifecycleMembershipToTracks(
      current.tracks,
      view,
      op,
      trackId,
      row,
    );
    // Skip no-op writes (keeps referential stability for untouched pages).
    if (
      nextTracks.length === current.tracks.length &&
      nextTracks.every((t, i) => t === current.tracks[i])
    ) {
      continue;
    }
    dispatch(
      tracksApi.util.upsertQueryData("listTracks", listArgs, {
        ...current,
        tracks: nextTracks,
      }),
    );
  }
}

/** Re-assert exclusive tab membership after lagging LIST refetches. */
export const SONG_LIBRARY_LIST_MEMBERSHIP_GUARD_DELAYS_MS = Object.freeze([
  0, 500, 2500, 5000, 10000,
]);

let songLibraryListMembershipGuardGeneration = 0;

export function scheduleSongLibraryListMembershipGuard(
  dispatch: typeof store.dispatch,
  args: {
    trackId: string;
    op: "archive" | "restore";
    row?: Track | null;
  },
) {
  const trackId = typeof args.trackId === "string" ? args.trackId.trim() : "";
  if (!trackId) return;
  const generation = ++songLibraryListMembershipGuardGeneration;
  for (const delayMs of SONG_LIBRARY_LIST_MEMBERSHIP_GUARD_DELAYS_MS) {
    window.setTimeout(() => {
      if (generation !== songLibraryListMembershipGuardGeneration) return;
      applyTrackLifecycleListMembership(dispatch, {
        trackId,
        op: args.op,
        row: args.row,
      });
    }, delayMs);
  }
}

/**
 * After Permanent Delete, delayed LIST invalidate can refetch lagging catalog
 * projection rows and resurrect deleted Songs. Re-assert removal on a ladder
 * so terminal BulkOp / single PD stay correct without browser refresh.
 */
let songLibraryDeleteRemovalGuardGeneration = 0;

export function scheduleSongLibraryDeleteRemovalGuard(
  dispatch: typeof store.dispatch,
  trackIds: string[],
) {
  const ids = Array.from(
    new Set(
      (trackIds || [])
        .map((id) => (typeof id === "string" ? id.trim() : ""))
        .filter((id) => id.length > 0),
    ),
  );
  if (ids.length === 0) return;
  const generation = ++songLibraryDeleteRemovalGuardGeneration;
  for (const delayMs of SONG_LIBRARY_LIST_MEMBERSHIP_GUARD_DELAYS_MS) {
    window.setTimeout(() => {
      if (generation !== songLibraryDeleteRemovalGuardGeneration) return;
      for (const trackId of ids) {
        applyTrackLifecycleListMembership(dispatch, {
          trackId,
          op: "delete",
        });
      }
    }, delayMs);
  }
}

function readCountSnapshot(): LifecycleCountSnapshot {
  const state = store.getState();
  const cachedArgs = tracksApi.util.selectCachedArgsForQuery(state, "countTracks");
  let active: number | null = null;
  let archived: number | null = null;
  let all: number | null = null;
  let byStatusActive: number | null = null;
  let byStatusArchived: number | null = null;

  for (const args of cachedArgs) {
    const entry = tracksApi.endpoints.countTracks.select(args)(state);
    const count = entry.data?.count;
    if (typeof count !== "number" || count < 0) continue;
    const status = (args && args.status) || entry.data?.status || "active";
    // Dedicated Active/Archived queries win. Never let status=all byStatus
    // overwrite them (reconcile can race with a lagging "all" payload).
    if (status === "active") active = count;
    else if (status === "archived") archived = count;
    else if (status === "all") all = count;
    if (entry.data?.byStatus) {
      if (typeof entry.data.byStatus.active === "number") {
        byStatusActive = entry.data.byStatus.active;
      }
      if (typeof entry.data.byStatus.archived === "number") {
        byStatusArchived = entry.data.byStatus.archived;
      }
    }
  }

  if (active == null && byStatusActive != null) active = byStatusActive;
  if (archived == null && byStatusArchived != null) archived = byStatusArchived;
  if (active == null) active = 0;
  if (archived == null) archived = 0;
  if (all == null || all === 0) {
    all = active + archived;
  }
  return { active, archived, all };
}

function patchCachedCounts(
  dispatch: typeof store.dispatch,
  op: LifecycleCountOp,
  times = 1,
) {
  const n = Math.max(1, Math.floor(times));
  const before = readCountSnapshot();
  let next = before;
  for (let i = 0; i < n; i += 1) {
    next = {
      active: applyLifecycleCountDelta(
        next.active,
        lifecycleCountDelta(op, "active"),
      ),
      archived: applyLifecycleCountDelta(
        next.archived,
        lifecycleCountDelta(op, "archived"),
      ),
      all: applyLifecycleCountDelta(next.all, lifecycleCountDelta(op, "all")),
    };
  }

  // Always write the three canonical tab caches (upsert so Active(0)→1 cannot
  // no-op when updateQueryData misses a subscription edge case).
  const writes: Array<{
    args: CountTracksArgs;
    data: CountTracksResponse;
  }> = [
    {
      args: { status: "active" },
      data: { count: next.active, exact: true, status: "active" },
    },
    {
      args: { status: "archived" },
      data: { count: next.archived, exact: true, status: "archived" },
    },
    {
      args: { status: "all" },
      data: {
        count: next.all,
        exact: true,
        status: "all",
        byStatus: { active: next.active, archived: next.archived },
      },
    },
  ];

  for (const write of writes) {
    dispatch(
      tracksApi.util.upsertQueryData("countTracks", write.args, write.data),
    );
  }

  // Keep any scoped count caches (album/artist/label) in sync when present.
  const cachedArgs = tracksApi.util.selectCachedArgsForQuery(
    store.getState(),
    "countTracks",
  );
  for (const args of cachedArgs) {
    if (!args || (!args.albumId && !args.artistId && !args.recordLabelId)) {
      continue;
    }
    dispatch(
      tracksApi.util.updateQueryData("countTracks", args, (draft) => {
        if (!draft || typeof draft.count !== "number" || draft.count < 0) return;
        const status = (args && args.status) || draft.status || "active";
        let count = draft.count;
        for (let i = 0; i < n; i += 1) {
          count = applyLifecycleCountDelta(
            count,
            lifecycleCountDelta(op, status),
          );
        }
        draft.count = count;
        if (draft.byStatus) {
          draft.byStatus = {
            active: next.active,
            archived: next.archived,
          };
        }
      }),
    );
  }
}

export const tracksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listTracks: builder.query<ListTracksResponse, ListTracksArgs | void>({
      query: (args) => {
        const params: Record<string, string | number> = {};
        if (args) {
          if (args.status) params.status = args.status;
          if (args.albumId) params.albumId = args.albumId;
          if (args.artistId) params.artistId = args.artistId;
          if (args.recordLabelId) params.recordLabelId = args.recordLabelId;
          if (args.genreId) {
            params.genreId = args.genreId;
            // subgenreId is only valid together with genreId.
            if (args.subgenreId) params.subgenreId = args.subgenreId;
          }
          if (args.trackId) params.trackId = args.trackId;
          if (args.limit) params.limit = args.limit;
          if (args.pageToken) params.pageToken = args.pageToken;
        }
        return { url: "/tracks", method: "GET", params };
      },
      transformResponse: (raw: ApiEnvelope<ListTracksResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to load tracks");
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.tracks
          ? [
              ...result.tracks.map((t) => ({ type: "Tracks" as const, id: t.trackId })),
              { type: "Tracks" as const, id: "LIST" },
            ]
          : [{ type: "Tracks" as const, id: "LIST" }],
    }),
    countTracks: builder.query<CountTracksResponse, CountTracksArgs | void>({
      query: (args) => {
        const params: Record<string, string | boolean> = { count: true };
        if (args?.status) params.status = args.status;
        if (args?.albumId) params.albumId = args.albumId;
        if (args?.artistId) params.artistId = args.artistId;
        if (args?.recordLabelId) params.recordLabelId = args.recordLabelId;
        return { url: "/tracks", method: "GET", params };
      },
      transformResponse: (raw: ApiEnvelope<CountTracksResponse>) => {
        if (
          !raw ||
          raw.success !== true ||
          !raw.data ||
          typeof raw.data.count !== "number"
        ) {
          return { count: -1, exact: true, status: "active" };
        }
        return raw.data;
      },
      // Separate from LIST so lifecycle LIST invalidation cannot overwrite
      // optimistic Active/Archived/All tab counts with an unguarded refetch.
      providesTags: [{ type: "Tracks" as const, id: "COUNT" }],
    }),
    listTrackSongHistory: builder.query<
      ListTrackSongHistoryResponse,
      ListTrackSongHistoryArgs | void
    >({
      query: (args) => {
        const params: Record<string, string | number | boolean> = {};
        if (args) {
          if (args.limit) params.limit = args.limit;
          if (args.pageToken) params.pageToken = args.pageToken;
          if (args.status) params.status = args.status;
          if (args.trackId) params.trackId = args.trackId;
          if (args.qTitle) params.qTitle = args.qTitle;
          if (args.qArtist) params.qArtist = args.qArtist;
          if (args.qUploader) params.qUploader = args.qUploader;
          if (args.firstUploadedFrom) params.firstUploadedFrom = args.firstUploadedFrom;
          if (args.firstUploadedTo) params.firstUploadedTo = args.firstUploadedTo;
          if (args.latestActivityFrom) params.latestActivityFrom = args.latestActivityFrom;
          if (args.latestActivityTo) params.latestActivityTo = args.latestActivityTo;
          if (args.latestActivityType) params.latestActivityType = args.latestActivityType;
          if (args.currentOutcome) params.currentOutcome = args.currentOutcome;
          if (args.deletedFrom) params.deletedFrom = args.deletedFrom;
          if (args.deletedTo) params.deletedTo = args.deletedTo;
          if (args.deletedBy) params.deletedBy = args.deletedBy;
          if (args.qDeletionReason) params.qDeletionReason = args.qDeletionReason;
        }
        // TracksHttpApi is mapped at /tracks → GET /track-song-history.
        return { url: "/tracks/track-song-history", method: "GET", params };
      },
      transformResponse: (raw: ApiEnvelope<ListTrackSongHistoryResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load song history",
          );
        }
        return raw.data;
      },
      providesTags: [{ type: "Tracks" as const, id: "SONG_HISTORY" }],
    }),
    countTrackSongHistory: builder.query<
      CountTrackSongHistoryResponse,
      Omit<ListTrackSongHistoryArgs, "limit" | "pageToken" | "count"> | void
    >({
      query: (args) => {
        const params: Record<string, string | boolean> = { count: true };
        if (args) {
          if (args.status) params.status = args.status;
          if (args.trackId) params.trackId = args.trackId;
          if (args.qTitle) params.qTitle = args.qTitle;
          if (args.qArtist) params.qArtist = args.qArtist;
          if (args.qUploader) params.qUploader = args.qUploader;
          if (args.firstUploadedFrom) params.firstUploadedFrom = args.firstUploadedFrom;
          if (args.firstUploadedTo) params.firstUploadedTo = args.firstUploadedTo;
          if (args.latestActivityFrom) params.latestActivityFrom = args.latestActivityFrom;
          if (args.latestActivityTo) params.latestActivityTo = args.latestActivityTo;
          if (args.latestActivityType) params.latestActivityType = args.latestActivityType;
          if (args.currentOutcome) params.currentOutcome = args.currentOutcome;
          if (args.deletedFrom) params.deletedFrom = args.deletedFrom;
          if (args.deletedTo) params.deletedTo = args.deletedTo;
          if (args.deletedBy) params.deletedBy = args.deletedBy;
          if (args.qDeletionReason) params.qDeletionReason = args.qDeletionReason;
        }
        return { url: "/tracks/track-song-history", method: "GET", params };
      },
      transformResponse: (raw: ApiEnvelope<CountTrackSongHistoryResponse>) => {
        if (
          !raw ||
          raw.success !== true ||
          !raw.data ||
          typeof raw.data.count !== "number"
        ) {
          return { count: -1, exact: false };
        }
        return raw.data;
      },
      providesTags: [{ type: "Tracks" as const, id: "SONG_HISTORY" }],
    }),
    countTrackSongHistoryByStatus: builder.query<
      SongHistoryCountsByStatusResponse,
      void
    >({
      query: () => ({
        url: "/tracks/track-song-history",
        method: "GET",
        params: { countsByStatus: true },
      }),
      transformResponse: (raw: ApiEnvelope<SongHistoryCountsByStatusResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          return { active: -1, archived: -1, deleted: -1, exact: false };
        }
        const d = raw.data;
        return {
          active: typeof d.active === "number" ? d.active : -1,
          archived: typeof d.archived === "number" ? d.archived : -1,
          deleted: typeof d.deleted === "number" ? d.deleted : -1,
          exact: d.exact === true,
        };
      },
      providesTags: [{ type: "Tracks" as const, id: "SONG_HISTORY" }],
    }),
    getTrackSongHistoryDetail: builder.query<
      SongHistoryDetailResponse,
      string
    >({
      query: (trackId) => ({
        url: `/tracks/track-song-history/${encodeURIComponent(trackId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<SongHistoryDetailResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load song history detail",
          );
        }
        return raw.data;
      },
      providesTags: (_result, _err, trackId) => [
        { type: "Tracks" as const, id: `SONG_HISTORY_${trackId}` },
        { type: "Tracks" as const, id: "SONG_HISTORY" },
      ],
    }),
    updateTrackGenre: builder.mutation<Track, UpdateTrackGenreArgs>({
      query: ({ trackId, ...patch }) => {
        const body: Record<string, string | null> = {};
        if ("genreId" in patch) body.genreId = patch.genreId ?? null;
        if ("subgenreId" in patch) body.subgenreId = patch.subgenreId ?? null;
        return {
          url: `/tracks/${encodeURIComponent(trackId)}`,
          method: "PATCH",
          body,
        };
      },
      transformResponse: (
        raw: ApiEnvelope<{ track?: Track } & Partial<Track>>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to update song genre",
          );
        }
        // Envelope may nest the row as { track } or return it directly.
        return (raw.data.track ?? raw.data) as Track;
      },
      async onQueryStarted({ trackId, ...patch }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Merge the new taxonomy fields into every cached list row —
          // no LIST invalidation (projection race; see lifecycle notes above).
          const cachedArgs = [
            ...tracksApi.util.selectCachedArgsForQuery(
              store.getState(),
              "listTracks",
            ),
          ];
          for (const listArgs of cachedArgs) {
            dispatch(
              tracksApi.util.updateQueryData("listTracks", listArgs, (draft) => {
                const row = draft.tracks?.find((t) => t.trackId === trackId);
                if (!row) return;
                if ("genreId" in patch) {
                  row.genreId = patch.genreId ?? null;
                  // Clearing genre clears subgenre.
                  if (patch.genreId == null) row.subgenreId = null;
                }
                if ("subgenreId" in patch) row.subgenreId = patch.subgenreId ?? null;
              }),
            );
          }
        } catch {
          // Mutation failed — leave list caches unchanged.
        }
      },
      invalidatesTags: () => [],
    }),
    archiveTrack: builder.mutation<ArchiveTrackResponse, ArchiveTrackArgs>({
      query: ({ trackId }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}`,
        method: "DELETE",
      }),
      transformResponse: (raw: ApiEnvelope<ArchiveTrackResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to archive track");
        }
        return raw.data;
      },
      async onQueryStarted({ trackId }, { dispatch, queryFulfilled }) {
        const hintJobId = findCachedTrackSourceJobId(trackId);
        try {
          const { data } = await queryFulfilled;
          const prior = findCachedTrackRow(trackId);
          const archivedRow: Track | null = prior
            ? {
                ...prior,
                status: "archived",
                archived: true,
                playable: false,
                archivedAt: data.deletedAt,
                deletedAt: data.deletedAt,
              }
            : null;
          applyTrackLifecycleListMembership(dispatch, {
            trackId,
            op: "archive",
            row: archivedRow,
          });
          patchCachedCounts(dispatch, "archive");
          scheduleSongLibraryListMembershipGuard(dispatch, {
            trackId,
            op: "archive",
            row: archivedRow,
          });
          // Lifecycle Mirror — refresh Song Processing Active/Archived.
          scheduleProcessingJobsInvalidateAfterSongLifecycleMirror(dispatch, {
            trackId,
            op: "archive",
            hintJobIds: hintJobId ? [hintJobId] : [],
          });
          // Song History summary projection — Active/Archived lists + tab counts.
          scheduleSongHistoryInvalidateAfterSongLifecycle(dispatch, {
            trackId,
            op: "archive",
          });
        } catch {
          // Mutation failed — leave list/count caches unchanged.
        }
      },
      // No immediate Tracks LIST invalidation — projection race overwrites optimistic counts.
      // ProcessingJobs LIST + Song History are refreshed via delayed invalidate ladders.
      invalidatesTags: () => [],
    }),
    restoreTrack: builder.mutation<RestoreTrackResponse, RestoreTrackArgs>({
      query: ({ trackId }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreTrackResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to restore track");
        }
        return raw.data;
      },
      async onQueryStarted({ trackId }, { dispatch, queryFulfilled }) {
        const hintJobId = findCachedTrackSourceJobId(trackId);
        try {
          const { data } = await queryFulfilled;
          const prior = findCachedTrackRow(trackId);
          const restoredRow: Track | null = prior
            ? {
                ...prior,
                status: "active",
                archived: false,
                playable: data.playable,
                restoredAt: data.restoredAt,
                archivedAt: data.archivedAt ?? prior.archivedAt,
                archivedBy: data.archivedBy ?? prior.archivedBy,
              }
            : null;
          applyTrackLifecycleListMembership(dispatch, {
            trackId,
            op: "restore",
            row: restoredRow,
          });
          patchCachedCounts(dispatch, "restore");
          scheduleSongLibraryListMembershipGuard(dispatch, {
            trackId,
            op: "restore",
            row: restoredRow,
          });
          // Lifecycle Mirror — refresh Song Processing Active/Archived.
          scheduleProcessingJobsInvalidateAfterSongLifecycleMirror(dispatch, {
            trackId,
            op: "restore",
            hintJobIds: hintJobId ? [hintJobId] : [],
          });
          // Song History summary projection — Active/Archived lists + tab counts.
          scheduleSongHistoryInvalidateAfterSongLifecycle(dispatch, {
            trackId,
            op: "restore",
          });
        } catch {
          // Mutation failed — leave list/count caches unchanged.
        }
      },
      invalidatesTags: () => [],
    }),
    getTrackChannelImpact: builder.query<TrackChannelImpact, string>({
      query: (trackId) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/channel-impact`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<TrackChannelImpact>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load channel programming impact",
          );
        }
        return raw.data;
      },
    }),
    preflightTrackChannelImpact: builder.mutation<
      TrackChannelImpact,
      { trackId: string }
    >({
      query: ({ trackId }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/channel-impact`,
        method: "POST",
        body: {},
      }),
      transformResponse: (raw: ApiEnvelope<TrackChannelImpact>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load channel programming impact",
          );
        }
        return raw.data;
      },
    }),
    preflightPermanentDeleteTrack: builder.mutation<
      PermanentDeletePreflight,
      { trackId: string }
    >({
      query: ({ trackId }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<PermanentDeletePreflight>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to preflight permanent delete",
          );
        }
        return raw.data;
      },
    }),
    permanentDeleteTrack: builder.mutation<
      PermanentDeleteResponse,
      PermanentDeleteArgs
    >({
      query: ({ trackId, confirmTitle, confirmTrackId, reason }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/permanent-delete`,
        method: "POST",
        body: {
          confirmTitle,
          confirmTrackId,
          confirm: "DELETE",
          reason,
        },
      }),
      transformResponse: (raw: ApiEnvelope<PermanentDeleteResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to permanently delete song",
          );
        }
        return raw.data;
      },
      async onQueryStarted({ trackId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          applyTrackLifecycleListMembership(dispatch, {
            trackId,
            op: "delete",
          });
          patchCachedCounts(dispatch, "delete");
          // Song History summary — Archived → Deleted + tab counts.
          scheduleSongHistoryInvalidateAfterSongLifecycle(dispatch, {
            trackId,
            op: "delete",
          });
        } catch {
          // leave caches unchanged
        }
      },
      // Song History refreshed via delayed invalidate ladder (not immediate LIST).
      invalidatesTags: () => [],
    }),
    bulkPermanentDeleteTracks: builder.mutation<
      BulkPermanentDeleteResponse,
      BulkPermanentDeleteArgs
    >({
      query: ({ ids, reason }) => ({
        url: "/tracks/permanent-delete",
        method: "POST",
        body: { ids, confirm: "DELETE", reason },
      }),
      transformResponse: (raw: ApiEnvelope<BulkPermanentDeleteResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Bulk permanent delete failed",
          );
        }
        return raw.data;
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const okIds = (data?.results || [])
            .filter((r) => r && r.ok && typeof r.trackId === "string")
            .map((r) => r.trackId);
          if (okIds.length > 0) {
            scheduleSongHistoryInvalidateAfterSongLifecycle(dispatch, {
              trackIds: okIds,
              op: "delete",
            });
          }
        } catch {
          // leave caches unchanged
        }
      },
      invalidatesTags: () => [],
    }),
    createTrackPermanentDeleteBulkOp: builder.mutation<
      TrackPermanentDeleteBulkOp,
      CreateTrackPermanentDeleteBulkOpArgs
    >({
      query: (body) => ({
        url: "/tracks/permanent-delete/bulk-operations",
        method: "POST",
        body: {
          ids: body.ids,
          confirm: "DELETE",
          reason: body.reason,
          selectionMode: body.selectionMode || "ids",
          idempotencyKey: body.idempotencyKey,
        },
      }),
      transformResponse: (raw: ApiEnvelope<TrackPermanentDeleteBulkOp>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to start permanent delete operation",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
    }),
    getTrackPermanentDeleteBulkOp: builder.query<
      TrackPermanentDeleteBulkOp,
      string
    >({
      query: (bulkOpId) => ({
        url: `/tracks/permanent-delete/bulk-operations/${encodeURIComponent(bulkOpId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<TrackPermanentDeleteBulkOp>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load permanent delete operation",
          );
        }
        return raw.data;
      },
    }),
    createSongHistoryTombstonePurge: builder.mutation<
      SongHistoryTombstonePurgeOp,
      CreateSongHistoryTombstonePurgeArgs
    >({
      query: (body) => ({
        url: "/tracks/track-song-history/tombstone-purge",
        method: "POST",
        body: {
          confirm: "PURGE",
          reason: body.reason,
          selectionMode: body.selectionMode,
          ids: body.ids,
          excludedIds: body.excludedIds,
          filter: body.filter,
          idempotencyKey: body.idempotencyKey,
        },
      }),
      transformResponse: (raw: ApiEnvelope<SongHistoryTombstonePurgeOp>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to start Tombstone Purge",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
    }),
    getSongHistoryTombstonePurge: builder.query<
      SongHistoryTombstonePurgeOp,
      string
    >({
      query: (bulkOpId) => ({
        url: `/tracks/track-song-history/tombstone-purge/${encodeURIComponent(bulkOpId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<SongHistoryTombstonePurgeOp>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load Tombstone Purge status",
          );
        }
        return raw.data;
      },
    }),
  }),
});

/** Generation counter — ignore stale in-flight reconcile responses. */
let tracksReconcileGeneration = 0;

export type ScheduleTracksReconcileOptions = {
  delayMs?: number;
  /** Lifecycle op used to detect stale (lagging) count responses. */
  op?: LifecycleCountOp;
  /** Track ids moved by this op — re-assert exclusive list membership. */
  trackIds?: string[];
};

/**
 * Delayed count reconcile after lifecycle success (projection catch-up).
 * Call AFTER optimistic count patches. Generation-guarded: if a newer
 * lifecycle mutation lands, older reconciles are ignored. Stale count
 * responses that regress vs the optimistic snapshot are rejected/retried.
 *
 * Archive/Restore must NOT invalidate Tracks LIST — a lagging archived/active
 * list refetch can put the same Song in both tabs. Membership is enforced by
 * applyTrackLifecycleListMembership + scheduleSongLibraryListMembershipGuard.
 * Permanent Delete may invalidate LIST after counts catch up.
 */
export function scheduleTracksListReconcile(
  dispatch: typeof store.dispatch,
  delayMsOrOpts: number | ScheduleTracksReconcileOptions = TRACKS_RECONCILE_DELAY_MS,
) {
  const opts: ScheduleTracksReconcileOptions =
    typeof delayMsOrOpts === "number"
      ? { delayMs: delayMsOrOpts }
      : delayMsOrOpts || {};
  const delayMs = opts.delayMs ?? TRACKS_RECONCILE_DELAY_MS;
  const op = opts.op;
  const trackIds = Array.from(
    new Set(
      (opts.trackIds || [])
        .map((id) => (typeof id === "string" ? id.trim() : ""))
        .filter((id) => id.length > 0),
    ),
  );

  // Snapshot already includes optimistic patches from the mutation.
  const expected = readCountSnapshot();
  const generation = ++tracksReconcileGeneration;

  const reassertListMembership = () => {
    if (op !== "archive" && op !== "restore") return;
    for (const trackId of trackIds) {
      applyTrackLifecycleListMembership(dispatch, { trackId, op });
    }
  };

  const attemptReconcile = (attempt: number) => {
    window.setTimeout(() => {
      if (generation !== tracksReconcileGeneration) return;

      void (async () => {
        if (generation !== tracksReconcileGeneration) return;
        try {
          const results = await Promise.all([
            dispatch(
              tracksApi.endpoints.countTracks.initiate(
                { status: "active" },
                { forceRefetch: true },
              ),
            ),
            dispatch(
              tracksApi.endpoints.countTracks.initiate(
                { status: "archived" },
                { forceRefetch: true },
              ),
            ),
            dispatch(
              tracksApi.endpoints.countTracks.initiate(
                { status: "all" },
                { forceRefetch: true },
              ),
            ),
          ]);
          if (generation !== tracksReconcileGeneration) return;

          if (op != null) {
            const statuses: Array<"active" | "archived" | "all"> = [
              "active",
              "archived",
              "all",
            ];
            let stale = false;
            for (let i = 0; i < statuses.length; i += 1) {
              const res = results[i];
              const data =
                res && "data" in res && res.data ? res.data : null;
              const count =
                data && typeof data.count === "number" ? data.count : -1;
              if (isStaleCountVersusExpected(statuses[i], count, expected, op)) {
                stale = true;
                break;
              }
              // status=all total is unchanged on restore/archive — also reject
              // lagging byStatus partitions from that payload.
              if (
                statuses[i] === "all" &&
                data &&
                data.byStatus &&
                (isStaleCountVersusExpected(
                  "active",
                  data.byStatus.active,
                  expected,
                  op,
                ) ||
                  isStaleCountVersusExpected(
                    "archived",
                    data.byStatus.archived,
                    expected,
                    op,
                  ))
              ) {
                stale = true;
                break;
              }
            }
            if (stale && attempt + 1 < TRACKS_RECONCILE_MAX_ATTEMPTS) {
              // Keep exclusive list membership while counts retry.
              reassertListMembership();
              attemptReconcile(attempt + 1);
              return;
            }
            if (stale) {
              // Keep optimistic expected totals; do not overwrite with lag.
              for (const status of statuses) {
                dispatch(
                  tracksApi.util.upsertQueryData("countTracks", { status }, {
                    count: expected[status],
                    exact: true,
                    status,
                    ...(status === "all"
                      ? {
                          byStatus: {
                            active: expected.active,
                            archived: expected.archived,
                          },
                        }
                      : {}),
                  }),
                );
              }
              reassertListMembership();
              return;
            }
          }

          // Counts look caught up. Re-assert exclusive tab membership for
          // Archive/Restore — do not invalidate LIST (stale list refetch can
          // resurrect the Song on the wrong tab). Delete still refreshes lists,
          // then re-asserts removal so lagging projections cannot resurrect rows.
          reassertListMembership();
          if (op === "delete" || op == null) {
            dispatch(
              tracksApi.util.invalidateTags([{ type: "Tracks", id: "LIST" }]),
            );
            if (op === "delete" && trackIds.length > 0) {
              scheduleSongLibraryDeleteRemovalGuard(dispatch, trackIds);
            }
          }
          // Song counts on Album / Artist / Record Label lists are
          // authoritative server fields — invalidate so they update live.
          dispatch(
            tracksApi.util.invalidateTags([
              { type: "Albums", id: "LIST" },
              { type: "Artists", id: "LIST" },
              { type: "RecordLabels", id: "LIST" },
            ]),
          );
        } catch {
          if (
            generation === tracksReconcileGeneration &&
            attempt + 1 < TRACKS_RECONCILE_MAX_ATTEMPTS
          ) {
            attemptReconcile(attempt + 1);
          }
        }
      })();
    }, attempt === 0 ? delayMs : TRACKS_RECONCILE_RETRY_MS);
  };

  attemptReconcile(0);
}

/** Optimistic count patch for permanent delete (MDL-3). */
export function patchTracksCountsForDelete(
  dispatch: typeof store.dispatch,
  times = 1,
) {
  patchCachedCounts(dispatch, "delete", times);
}

/** Remove deleted tracks from cached list pages. */
export function patchTracksListsRemove(
  dispatch: typeof store.dispatch,
  trackIds: string[],
) {
  for (const trackId of trackIds) {
    applyTrackLifecycleListMembership(dispatch, {
      trackId,
      op: "delete",
    });
  }
}

export function extractTracksErrorMessage(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const data = anyErr.data as Record<string, unknown> | undefined;
  if (data && typeof data.message === "string") {
    const details = data.details as Record<string, unknown> | undefined;
    if (
      details &&
      details.verification &&
      typeof details.verification === "object"
    ) {
      const verification = details.verification as {
        failures?: Array<{ message?: string }>;
      };
      const first =
        verification.failures &&
        verification.failures[0] &&
        verification.failures[0].message;
      if (first) return `${data.message}: ${first}`;
    }
    return data.message;
  }
  if (typeof anyErr.message === "string") return anyErr.message;
  if (typeof anyErr.error === "string") return anyErr.error;
  return null;
}

/**
 * Batch Archive/Restore: treat "already in desired state" 409s as success so
 * operators do not see false failure toasts when the end state is correct.
 */
export function isBenignTrackLifecycleConflict(
  action: "archive" | "restore",
  err: unknown,
): boolean {
  const msg = (extractTracksErrorMessage(err) || "").toLowerCase();
  if (action === "archive") {
    return msg.includes("already archived");
  }
  // Restore of an Active track: "not archived". Exclude explicit deleted wording.
  if (action === "restore") {
    return msg.includes("not archived") && !msg.includes("deleted");
  }
  return false;
}

export function bulkDeleteFailuresFromResponse(
  res: BulkPermanentDeleteResponse,
): BulkPermanentDeleteFailure[] {
  return (res.results || [])
    .filter((r) => !r.ok)
    .map((r) => ({
      trackId: r.trackId,
      title: r.title ?? null,
      ok: false as const,
      skipped: r.skipped,
      message: r.message || "Permanent delete failed",
      retryable: r.retryable !== false,
      details: r.details,
    }));
}

export const {
  useListTracksQuery,
  useCountTracksQuery,
  useListTrackSongHistoryQuery,
  useCountTrackSongHistoryQuery,
  useCountTrackSongHistoryByStatusQuery,
  useGetTrackSongHistoryDetailQuery,
  useUpdateTrackGenreMutation,
  useArchiveTrackMutation,
  useRestoreTrackMutation,
  useGetTrackChannelImpactQuery,
  useLazyGetTrackChannelImpactQuery,
  usePreflightTrackChannelImpactMutation,
  usePreflightPermanentDeleteTrackMutation,
  usePermanentDeleteTrackMutation,
  useBulkPermanentDeleteTracksMutation,
  useCreateTrackPermanentDeleteBulkOpMutation,
  useLazyGetTrackPermanentDeleteBulkOpQuery,
  useCreateSongHistoryTombstonePurgeMutation,
  useLazyGetSongHistoryTombstonePurgeQuery,
} = tracksApi;
